import { useState, useEffect, useCallback } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  Package,
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  X,
  Plus,
  Search,
  Edit,
  Trash2,
  Upload,
  MapPin,
  TrendingUp,
  Truck,
  CheckCircle,
  Clock,
  ChevronRight,
  FileText
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../App";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: "Pending", color: "bg-amber-100 text-amber-700" },
  approved: { label: "Approved", color: "bg-green-100 text-green-700" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700" },
  order_placed: { label: "Order Placed", color: "bg-indigo-100 text-indigo-700" },
  processing: { label: "Processing", color: "bg-amber-100 text-amber-700" },
  in_transit: { label: "In Transit", color: "bg-blue-100 text-blue-700" },
  out_for_delivery: { label: "Out for Delivery", color: "bg-emerald-100 text-emerald-700" },
  delivered: { label: "Delivered", color: "bg-green-500 text-white" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700" }
};

// Sidebar Component
const Sidebar = ({ open, setOpen }) => {
  const location = useLocation();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const menuItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard" },
    { icon: Package, label: "Shipments", path: "/admin/shipments" },
    { icon: FileText, label: "Requests", path: "/admin/requests" },
    { icon: MessageSquare, label: "Messages", path: "/admin/messages" },
    { icon: Settings, label: "Settings", path: "/admin/settings" },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
    toast.success("Logged out successfully");
  };

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-slate-900 transform transition-transform duration-300 lg:translate-x-0 ${
        open ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-800">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-blue-600 rounded-sm flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>
                Swift<span className="text-blue-400">Logix</span>
              </span>
            </Link>
          </div>

          {/* Nav Links */}
          <nav className="flex-1 p-4 space-y-1">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                    isActive 
                      ? 'bg-blue-600/20 text-blue-400 border-l-2 border-blue-400' 
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`}
                  data-testid={`sidebar-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-slate-800">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 w-full rounded-sm text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Dashboard Home Component
const DashboardHome = () => {
  const [stats, setStats] = useState(null);
  const [recentShipments, setRecentShipments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, shipmentsRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
          axios.get(`${API}/shipments`, { withCredentials: true })
        ]);
        setStats(statsRes.data);
        setRecentShipments(shipmentsRes.data.slice(0, 5));
      } catch (error) {
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const statCards = [
    { label: "Total Shipments", value: stats?.total_shipments || 0, icon: Package, color: "bg-blue-500" },
    { label: "Active Shipments", value: stats?.active_shipments || 0, icon: Truck, color: "bg-amber-500" },
    { label: "Delivered Today", value: stats?.delivered_today || 0, icon: CheckCircle, color: "bg-green-500" },
    { label: "Pending Messages", value: stats?.pending_contacts || 0, icon: MessageSquare, color: "bg-purple-500" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-home">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>Dashboard</h1>
        <p className="text-slate-500">Overview of your logistics operations</p>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-sm border border-slate-200 p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{stat.label}</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 ${stat.color} rounded-sm flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Recent Shipments */}
      <div className="bg-white rounded-sm border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-200 flex justify-between items-center">
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>Recent Shipments</h2>
          <Link to="/admin/shipments">
            <Button variant="ghost" size="sm" className="text-blue-600">
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tracking ID</TableHead>
                <TableHead>Destination</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentShipments.map((shipment) => (
                <TableRow key={shipment.tracking_id}>
                  <TableCell className="font-medium">{shipment.tracking_id}</TableCell>
                  <TableCell>{shipment.destination}</TableCell>
                  <TableCell>
                    <Badge className={statusConfig[shipment.status]?.color}>
                      {statusConfig[shipment.status]?.label}
                    </Badge>
                  </TableCell>
                  <TableCell>{new Date(shipment.created_at).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

// Shipments Management Component
const ShipmentsManagement = () => {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isTrackingDialogOpen, setIsTrackingDialogOpen] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [formData, setFormData] = useState({
    tracking_id: "",
    sender_name: "",
    sender_address: "",
    receiver_name: "",
    receiver_address: "",
    package_description: "",
    weight: "",
    service_type: "standard",
    origin: "",
    destination: "",
    estimated_delivery: ""
  });
  const [trackingData, setTrackingData] = useState({
    status: "",
    location: "",
    description: ""
  });

  const fetchShipments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/shipments`, { withCredentials: true });
      setShipments(response.data);
    } catch (error) {
      toast.error("Failed to load shipments");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchShipments();
  }, [fetchShipments]);

  const handleAddShipment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/shipments`, {
        ...formData,
        weight: parseFloat(formData.weight)
      }, { withCredentials: true });
      toast.success("Shipment created successfully");
      setIsAddDialogOpen(false);
      resetForm();
      fetchShipments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create shipment");
    }
  };

  const handleUpdateShipment = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/shipments/${selectedShipment.tracking_id}`, {
        ...formData,
        weight: parseFloat(formData.weight)
      }, { withCredentials: true });
      toast.success("Shipment updated successfully");
      setIsEditDialogOpen(false);
      fetchShipments();
    } catch (error) {
      toast.error("Failed to update shipment");
    }
  };

  const handleDeleteShipment = async (trackingId) => {
    if (!window.confirm("Are you sure you want to delete this shipment?")) return;
    try {
      await axios.delete(`${API}/shipments/${trackingId}`, { withCredentials: true });
      toast.success("Shipment deleted successfully");
      fetchShipments();
    } catch (error) {
      toast.error("Failed to delete shipment");
    }
  };

  const handleAddTracking = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/shipments/${selectedShipment.tracking_id}/tracking`, trackingData, { withCredentials: true });
      toast.success("Tracking update added");
      setIsTrackingDialogOpen(false);
      setTrackingData({ status: "", location: "", description: "" });
      fetchShipments();
    } catch (error) {
      toast.error("Failed to add tracking update");
    }
  };

  const handleFileUpload = async (trackingId, fileType, file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API}/shipments/${trackingId}/upload/${fileType}`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`${fileType.toUpperCase()} uploaded successfully`);
      fetchShipments();
    } catch (error) {
      toast.error(`Failed to upload ${fileType}`);
    }
  };

  const resetForm = () => {
    setFormData({
      tracking_id: "",
      sender_name: "",
      sender_address: "",
      receiver_name: "",
      receiver_address: "",
      package_description: "",
      weight: "",
      service_type: "standard",
      origin: "",
      destination: "",
      estimated_delivery: ""
    });
  };

  const openEditDialog = (shipment) => {
    setSelectedShipment(shipment);
    setFormData({
      tracking_id: shipment.tracking_id,
      sender_name: shipment.sender_name,
      sender_address: shipment.sender_address,
      receiver_name: shipment.receiver_name,
      receiver_address: shipment.receiver_address,
      package_description: shipment.package_description,
      weight: shipment.weight.toString(),
      service_type: shipment.service_type,
      origin: shipment.origin,
      destination: shipment.destination,
      estimated_delivery: shipment.estimated_delivery || ""
    });
    setIsEditDialogOpen(true);
  };

  const filteredShipments = shipments.filter(s => 
    s.tracking_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.receiver_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.destination.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="shipments-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>Shipments</h1>
          <p className="text-slate-500">Manage all shipments</p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-blue-600 hover:bg-blue-700" data-testid="add-shipment-button">
              <Plus className="w-4 h-4 mr-2" /> Add Shipment
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Shipment</DialogTitle>
              <DialogDescription>Fill in the shipment details below.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddShipment} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tracking ID</Label>
                  <Input
                    placeholder="SWL-2024-XXX"
                    value={formData.tracking_id}
                    onChange={(e) => setFormData({...formData, tracking_id: e.target.value})}
                    required
                    data-testid="shipment-tracking-id-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Service Type</Label>
                  <Select value={formData.service_type} onValueChange={(v) => setFormData({...formData, service_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="express">Express</SelectItem>
                      <SelectItem value="freight">Freight</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sender Name</Label>
                  <Input
                    placeholder="John Doe"
                    value={formData.sender_name}
                    onChange={(e) => setFormData({...formData, sender_name: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Receiver Name</Label>
                  <Input
                    placeholder="Jane Smith"
                    value={formData.receiver_name}
                    onChange={(e) => setFormData({...formData, receiver_name: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sender Address</Label>
                  <Input
                    placeholder="123 Main St, City"
                    value={formData.sender_address}
                    onChange={(e) => setFormData({...formData, sender_address: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Receiver Address</Label>
                  <Input
                    placeholder="456 Oak Ave, City"
                    value={formData.receiver_address}
                    onChange={(e) => setFormData({...formData, receiver_address: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Origin</Label>
                  <Input
                    placeholder="New York, NY"
                    value={formData.origin}
                    onChange={(e) => setFormData({...formData, origin: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Destination</Label>
                  <Input
                    placeholder="Los Angeles, CA"
                    value={formData.destination}
                    onChange={(e) => setFormData({...formData, destination: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Package Description</Label>
                  <Input
                    placeholder="Electronics, Fragile"
                    value={formData.package_description}
                    onChange={(e) => setFormData({...formData, package_description: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Weight (kg)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="5.5"
                    value={formData.weight}
                    onChange={(e) => setFormData({...formData, weight: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Estimated Delivery Date</Label>
                <Input
                  type="date"
                  value={formData.estimated_delivery}
                  onChange={(e) => setFormData({...formData, estimated_delivery: e.target.value})}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">Create Shipment</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Search shipments..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 rounded-sm"
          data-testid="shipment-search-input"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-sm border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tracking ID</TableHead>
                <TableHead>Receiver</TableHead>
                <TableHead>Route</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Documents</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="spinner w-6 h-6 mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : filteredShipments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    No shipments found
                  </TableCell>
                </TableRow>
              ) : (
                filteredShipments.map((shipment) => (
                  <TableRow key={shipment.tracking_id}>
                    <TableCell className="font-medium">{shipment.tracking_id}</TableCell>
                    <TableCell>{shipment.receiver_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <span>{shipment.origin}</span>
                        <ChevronRight className="w-3 h-3 text-slate-400" />
                        <span>{shipment.destination}</span>
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">{shipment.service_type}</TableCell>
                    <TableCell>
                      <Badge className={statusConfig[shipment.status]?.color} data-testid="shipment-status-badge">
                        {statusConfig[shipment.status]?.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {shipment.pod_file && <Badge variant="outline" className="text-xs">POD</Badge>}
                        {shipment.invoice_file && <Badge variant="outline" className="text-xs">INV</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            Actions
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(shipment)}>
                            <Edit className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setSelectedShipment(shipment); setIsTrackingDialogOpen(true); }}>
                            <MapPin className="w-4 h-4 mr-2" /> Update Tracking
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <label className="cursor-pointer flex items-center">
                              <Upload className="w-4 h-4 mr-2" /> Upload POD
                              <input
                                type="file"
                                className="hidden"
                                accept=".pdf,.jpg,.png"
                                onChange={(e) => e.target.files?.[0] && handleFileUpload(shipment.tracking_id, 'pod', e.target.files[0])}
                              />
                            </label>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <label className="cursor-pointer flex items-center">
                              <FileText className="w-4 h-4 mr-2" /> Upload Invoice
                              <input
                                type="file"
                                className="hidden"
                                accept=".pdf,.jpg,.png"
                                onChange={(e) => e.target.files?.[0] && handleFileUpload(shipment.tracking_id, 'invoice', e.target.files[0])}
                              />
                            </label>
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => handleDeleteShipment(shipment.tracking_id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Shipment</DialogTitle>
            <DialogDescription>Update shipment details.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateShipment} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tracking ID</Label>
                <Input value={formData.tracking_id} disabled />
              </div>
              <div className="space-y-2">
                <Label>Service Type</Label>
                <Select value={formData.service_type} onValueChange={(v) => setFormData({...formData, service_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="express">Express</SelectItem>
                    <SelectItem value="freight">Freight</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sender Name</Label>
                <Input
                  value={formData.sender_name}
                  onChange={(e) => setFormData({...formData, sender_name: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Receiver Name</Label>
                <Input
                  value={formData.receiver_name}
                  onChange={(e) => setFormData({...formData, receiver_name: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sender Address</Label>
                <Input
                  value={formData.sender_address}
                  onChange={(e) => setFormData({...formData, sender_address: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Receiver Address</Label>
                <Input
                  value={formData.receiver_address}
                  onChange={(e) => setFormData({...formData, receiver_address: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Origin</Label>
                <Input
                  value={formData.origin}
                  onChange={(e) => setFormData({...formData, origin: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Destination</Label>
                <Input
                  value={formData.destination}
                  onChange={(e) => setFormData({...formData, destination: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Package Description</Label>
                <Input
                  value={formData.package_description}
                  onChange={(e) => setFormData({...formData, package_description: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Weight (kg)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.weight}
                  onChange={(e) => setFormData({...formData, weight: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Estimated Delivery Date</Label>
              <Input
                type="date"
                value={formData.estimated_delivery}
                onChange={(e) => setFormData({...formData, estimated_delivery: e.target.value})}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tracking Update Dialog */}
      <Dialog open={isTrackingDialogOpen} onOpenChange={setIsTrackingDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Tracking Status</DialogTitle>
            <DialogDescription>Add a new tracking update for {selectedShipment?.tracking_id}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddTracking} className="space-y-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={trackingData.status} onValueChange={(v) => setTrackingData({...trackingData, status: v})}>
                <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="order_placed">Order Placed</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="in_transit">In Transit</SelectItem>
                  <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Location</Label>
              <Input
                placeholder="City, State/Country"
                value={trackingData.location}
                onChange={(e) => setTrackingData({...trackingData, location: e.target.value})}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Package has arrived at distribution center..."
                value={trackingData.description}
                onChange={(e) => setTrackingData({...trackingData, description: e.target.value})}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsTrackingDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">Add Update</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Messages Component
const MessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await axios.get(`${API}/contacts`, { withCredentials: true });
        setMessages(response.data);
      } catch (error) {
        toast.error("Failed to load messages");
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  }, []);

  return (
    <div className="space-y-6" data-testid="messages-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>Messages</h1>
        <p className="text-slate-500">Contact form submissions</p>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 shadow-sm">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            No messages yet
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {messages.map((msg) => (
              <div key={msg.id} className="p-6">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-semibold text-slate-900">{msg.name}</h3>
                    <p className="text-sm text-slate-500">{msg.email} {msg.phone && `• ${msg.phone}`}</p>
                  </div>
                  <span className="text-xs text-slate-400">
                    {new Date(msg.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm font-medium text-slate-700 mb-1">{msg.subject}</p>
                <p className="text-sm text-slate-600">{msg.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Settings Page
const SettingsPage = () => {
  const { user } = useAuth();

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>Settings</h1>
        <p className="text-slate-500">Manage your account</p>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 shadow-sm p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Account Information</h2>
        <div className="space-y-4">
          <div>
            <Label className="text-slate-500">Name</Label>
            <p className="font-medium">{user?.name}</p>
          </div>
          <div>
            <Label className="text-slate-500">Email</Label>
            <p className="font-medium">{user?.email}</p>
          </div>
          <div>
            <Label className="text-slate-500">Role</Label>
            <p className="font-medium capitalize">{user?.role}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Shipment Requests Admin Component
const ShipmentRequestsAdmin = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");

  const fetchRequests = useCallback(async () => {
    try {
      const url = filterStatus === "all" 
        ? `${API}/admin/shipment-requests`
        : `${API}/admin/shipment-requests?status=${filterStatus}`;
      const response = await axios.get(url, { withCredentials: true });
      setRequests(response.data);
    } catch (error) {
      toast.error("Failed to load requests");
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleApprove = async (requestId) => {
    if (!window.confirm("Approve this request and create a shipment?")) return;
    try {
      await axios.put(`${API}/admin/shipment-requests/${requestId}/approve`, {}, { withCredentials: true });
      toast.success("Request approved! Shipment created.");
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve request");
    }
  };

  const handleReject = async (requestId) => {
    if (!window.confirm("Reject this shipment request?")) return;
    try {
      await axios.put(`${API}/admin/shipment-requests/${requestId}/reject`, {}, { withCredentials: true });
      toast.success("Request rejected");
      fetchRequests();
    } catch (error) {
      toast.error("Failed to reject request");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-requests-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>Shipment Requests</h1>
          <p className="text-slate-500">Review and approve customer shipment requests</p>
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Requests</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Requests Table */}
      <div className="bg-white rounded-sm border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Request ID</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Pickup</TableHead>
                <TableHead>Delivery</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Weight</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <div className="spinner w-6 h-6 mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                    No shipment requests found
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((req) => (
                  <TableRow key={req.request_id}>
                    <TableCell className="font-medium">{req.request_id}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{req.customer_name}</p>
                        <p className="text-xs text-slate-500">{req.customer_company || req.customer_email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[120px] truncate text-sm">{req.pickup_address}</TableCell>
                    <TableCell className="max-w-[120px] truncate text-sm">{req.delivery_address}</TableCell>
                    <TableCell className="capitalize">{req.service_type}</TableCell>
                    <TableCell>{req.weight} kg</TableCell>
                    <TableCell>
                      <Badge className={statusConfig[req.status]?.color}>
                        {statusConfig[req.status]?.label}
                      </Badge>
                    </TableCell>
                    <TableCell>{new Date(req.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      {req.status === "pending" ? (
                        <div className="flex justify-end gap-2">
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleApprove(req.request_id)}
                            data-testid={`approve-request-${req.request_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => handleReject(req.request_id)}
                            data-testid={`reject-request-${req.request_id}`}
                          >
                            Reject
                          </Button>
                        </div>
                      ) : req.tracking_id ? (
                        <Link to={`/tracking?id=${req.tracking_id}`} className="text-blue-600 hover:underline text-sm">
                          {req.tracking_id}
                        </Link>
                      ) : (
                        <span className="text-slate-400 text-sm">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

// Main Admin Dashboard Component
const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 py-4">
            <button
              className="lg:hidden p-2 text-slate-600"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-sidebar-toggle"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex items-center gap-4 ml-auto">
              <span className="text-sm text-slate-600">
                Welcome, <span className="font-medium">{user?.name}</span>
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Routes>
            <Route path="dashboard" element={<DashboardHome />} />
            <Route path="shipments" element={<ShipmentsManagement />} />
            <Route path="requests" element={<ShipmentRequestsAdmin />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="*" element={<DashboardHome />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
